import { MEMORY_UNIT_SUFFIXES } from '@cast/constants';
import { MemoryUnits, Memory } from '@cast/shared-types';

export type MemoryUnit = (typeof MEMORY_UNIT_SUFFIXES)[number];

const memoryUnits: Record<MemoryUnit, number> = {
  Bytes: 1,
  KiB: 1024,
  MiB: 1024 ** 2,
  GiB: 1024 ** 3,
  TiB: 1024 ** 4,
  PiB: 1024 ** 5,
  EiB: 1024 ** 6,
  ZiB: 1024 ** 7,
  YiB: 1024 ** 8,
};

export const convertUnit = (
  value: number,
  fromUnit: MemoryUnit,
  toUnit: MemoryUnit
): number => {
  if (fromUnit === toUnit) {
    return value;
  }

  const fromUnitFactor = memoryUnits[fromUnit];
  const toUnitFactor = memoryUnits[toUnit];

  return (value * fromUnitFactor) / toUnitFactor;
};

export const bytesToGiBiBytes = (bytes: number): number =>
  convertUnit(bytes, 'Bytes', 'GiB');

export type TimeUnit = 'µs' | 'ms' | 's';

const TIME_UNIT_INDEX: Record<TimeUnit, number> = {
  µs: 0,
  ms: 1,
  s: 2,
};

type NormalizeMillisOptions = {
  /** Number of decimal places to consider for µs conversion (default: 0) */
  roundingBreakpoint?: number;
  /** Smallest unit allowed (e.g., 'ms' prevents conversion to µs) */
  minGranularity?: 'µs' | 'ms';
  /** Largest unit allowed (e.g., 's' allows conversion to seconds) */
  maxGranularity?: 'ms' | 's';
};

/**
 * Normalizes a millisecond value to the most appropriate time unit.
 *
 * By default, converts to µs when the value would round to zero at the given breakpoint.
 * Use `minGranularity` and `maxGranularity` to control the range of allowed units.
 */
export const normalizeMillis = (
  value: number,
  options?: NormalizeMillisOptions
): { value: number; unit: TimeUnit } => {
  const maxIdx = TIME_UNIT_INDEX[options?.maxGranularity ?? 'ms'];

  if (maxIdx >= 2 && value >= 1000) {
    return { value: value / 1000, unit: 's' };
  }

  const minIdx = TIME_UNIT_INDEX[options?.minGranularity ?? 'µs'];

  if (
    minIdx === 0 &&
    value > 0 &&
    value < 1 / 10 ** (options?.roundingBreakpoint ?? 0)
  ) {
    return { value: value * 1000, unit: 'µs' };
  }

  return { value, unit: 'ms' };
};

export const convertMillisToUnit = (
  valueInMillis: number,
  unit: TimeUnit
): number => {
  switch (unit) {
    case 's':
      return valueInMillis / 1000;
    case 'µs':
      return valueInMillis * 1000;
    default:
      return valueInMillis;
  }
};

export const normalizeMemoryUnit = ({ amount, unit }: Memory): Memory => {
  let index = MEMORY_UNIT_SUFFIXES.indexOf(unit);
  let toUnit = unit;
  let val = amount;

  if (amount < 1) {
    // Current value is already smaller than 1, try smaller units until we get whole number
    index--;
    if (index < 0) {
      return { amount, unit };
    }
    while (index >= 0) {
      toUnit = MEMORY_UNIT_SUFFIXES[index];
      val = convertUnit(amount, unit, toUnit);
      if (val >= 1) {
        return { amount: val, unit: toUnit };
      }
      index--;
    }
    return { amount: val, unit: toUnit };
  }
  // Current value is larger than 1, try larger units until we reach number smaller than 1, then return previous value
  index++;
  while (index < MEMORY_UNIT_SUFFIXES.length) {
    const prevUnit = toUnit;
    const prevVal = val;
    toUnit = MEMORY_UNIT_SUFFIXES[index];
    val = convertUnit(amount, unit, toUnit);
    if (val < 1) {
      return { amount: prevVal, unit: prevUnit };
    }
    index++;
  }
  return { amount: val, unit: toUnit };
};

export const bytesToUnit = (
  bytes: number,
  decimals = 0
): [number, MemoryUnits] => {
  if (bytes === 0) {
    return [0, 'Bytes'];
  }
  const normalized = normalizeMemoryUnit({ amount: bytes, unit: 'Bytes' });
  return [Number(normalized.amount.toFixed(decimals)), normalized.unit];
};

export const isMemoryUnit = (unit: unknown): unit is MemoryUnit => {
  return MEMORY_UNIT_SUFFIXES.includes(unit as MemoryUnit);
};
